import React from 'react';
import { BrowserRouter as Router } from 'react-router-dom';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  ConfigProvider as AntdConfigProvider,
  message as AntdMessage,
} from 'antd';
import frFR from 'antd/lib/locale/fr_FR';
import moment from 'moment';

import { Authenticator } from 'Authenticator/Authenticator';
import ExportFiles from 'components/DownloadableFiles/DownloadableFiles';
import Footer from 'components/Footer';
import Header from 'components/Header/Header';
import { InformationMessagesDisplay } from 'components/InformationMessageDisplay/InformationMessagesDisplay';
import Maintenance from 'components/Maintenance/Maintenance';
import RedirectHandler from 'components/RedirectHandler/RedirectHandler';
import {
  Environnement,
  EnvironnementProvider,
} from 'hooks/EnvironementStoreContext';
import { unsafeCast } from 'typeGuards/typeGuards';

import AppRoutes from './AppRoutes';

import './App.less';

import 'moment/locale/fr';

function parseKey<T>(toParse: unknown, searchedKey: string): T | null {
  if (typeof toParse === 'object' && toParse !== null) {
    for (const [key, value] of Object.entries(toParse)) {
      if (key === searchedKey) {
        return unsafeCast(value);
      }
    }
  }
  return null;
}

function checkEnv(): Environnement | null {
  const windowEnv: Environnement | null = parseKey(globalThis, '_env_');
  if (windowEnv !== null) {
    // Create a copy to avoid mutating window._env_
    const env: Environnement = { ...windowEnv } as Environnement;

    // Convert *_BOOL values from string to boolean
    const booleanKeys: (keyof Environnement)[] = (
      Object.keys(windowEnv) as (keyof Environnement)[]
    ).filter(key => key.endsWith('_BOOL'));
    for (const key of booleanKeys) {
      (env[key] as boolean) = 'true' === parseKey<string>(windowEnv, key);
    }

    const backendUrl: unknown | null = parseKey(env, 'BACKEND_URL');
    if (backendUrl !== null && typeof backendUrl === 'string') {
      return env;
    }
  }

  return null;
}

function setupAntdConfig(): void {
  /* 125 is the banner's height */
  AntdMessage.config({ top: 125, duration: 1.5 });

  moment.locale('fr');
}

const App: React.FC = () => {
  const env = checkEnv();
  setupAntdConfig();

  const queryClient = new QueryClient();

  if (env === null) {
    //  eslint-disable-next-line
    return <></>; // TODO : display proper error page (env is not configured => contact server admin)
  } else {
    return (
      <AntdConfigProvider locale={frFR}>
        <EnvironnementProvider value={env}>
          <QueryClientProvider client={queryClient}>
            <Router>
              <Maintenance>
                <Authenticator>
                  <ExportFiles>
                    <div className={'Container'}>
                      <Header className={'Header'} />
                      <InformationMessagesDisplay>
                        <RedirectHandler>
                          <main className={'Main'}>
                            <AppRoutes />
                          </main>
                        </RedirectHandler>
                        <Footer className={'Footer'} />
                      </InformationMessagesDisplay>
                    </div>
                  </ExportFiles>
                </Authenticator>
              </Maintenance>
            </Router>
            {process.env.REACT_APP_QUERY_DEV_TOOLS === 'true' && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}
          </QueryClientProvider>
        </EnvironnementProvider>
      </AntdConfigProvider>
    );
  }
};

export default App;
